import * as React from 'react'
import PropTypes from 'prop-types'
import * as api from '../instantAuditsAdminApi'
import _, {cloneDeep, difference} from 'lodash'
import {buildUpdate, defaultConfig, hideClass, isNullyField} from "./sharedFunctions";
import {defaultRateLimits, requestTypes} from "./data";
import {RateLimits} from "./RateLimits";
import './instantAuditsConfig.scss'

export class InstantAuditsConfigEditor extends React.Component {
  static propTypes = {
    accountId: PropTypes.string,
    accountIds: PropTypes.array,
    onModelChange: PropTypes.func
  }

  state = {
    isLoading: true,
    updates: [],
    configs: [],
    activeKey: 1,
    formInvalidator: null,
    formValid: true,
    validationErrors: {
      invalidEmptyHostname: false,
      hostnameOrApiMaxExceeded: false
    },
    dirty: false,
  }

  onChange = (update) => {
    this.setState((state) => {
      return {configs: this.updateNested(update, state, null, 'update').configs, dirty: true}
    }, this.onModelChange)
  }

  onRemove = (update) => {
    this.setState((state) => {
      return {configs: this.updateNested(update, state, null, 'remove').configs, dirty: true}
    }, this.onModelChange)
  }

  onAdd = (update) => {
    this.setState((state) => {
      return {configs: this.updateNested(update, state, null, 'add').configs, dirty: true}
    }, this.onModelChange)
  }

  runValidations = (callback) => {
    const hostnameOrApiMaxExceeded = this.hostnameOrApiMaxExceeded()
    const invalidEmptyHostname = this.invalidEmptyHostname()
    const formValid = !hostnameOrApiMaxExceeded && !invalidEmptyHostname
    this.setState({formValid, validationErrors: {hostnameOrApiMaxExceeded, invalidEmptyHostname}},callback)
  }

  updateNested = (update, obj, prevIndex, type) => {
    const key = update && !update.isFinal && Object.keys(update).find(item => item !== "index")

    if (update.isFinal) {
      if (type === 'update') {
        return obj.map((item, thisIndex) => prevIndex !== thisIndex ? item : {...item, ...update.value, dirty: true})
      } else if (type === 'add') {
        return [...obj, {...update.value, dirty: true}]
      } else if (type === 'remove') {
        return obj.map((item, thisIndex) => prevIndex !== thisIndex ? item : {...item, deleted: true})
      }
    }
    else if (Array.isArray(obj)) {
      return obj.map((item, thisIndex) => prevIndex !== thisIndex ? item : {
        ...item,
        [key]: this.updateNested(update[key], item[key], update.index, type)
      })
    } else {
      return {...obj, [key]: this.updateNested(update[key], obj[key], update.index, type)}
    }
  }

  addConfig = () => {
    this.onAdd({configs: {value: defaultConfig(this.state.accountId, this.state.configs.filter(config => !config.deleted)), isFinal: true}})
  }

  onModelChange = () => {
    this.runValidations(
      () => this.props.onModelChange({...this.state})
    )
  }

  toggleCollapse = (index) => {
    this.setState(state => {
      const {configs} = state
      const newConfigs = [...configs]
      newConfigs[index].collapse = !newConfigs[index].collapse
      return {configs: newConfigs}
    })
  }

  componentWillReceiveProps(nextProps, nextContext) {
    const {accountId} = nextProps
    if (accountId !== this.state.accountId) {
      this.setState({accountId}, () => {
        api.configs([], [accountId]).then(resp => {
          const configs = cloneDeep(resp.data.result).map( config => ({...config, apiChecked: isNullyField(config.hostname) }))
          this.setState({configs, isLoading: false})
        })
      })
    }
  }

  availableRequestTypes = (rateLimits) => {
    return difference(requestTypes, rateLimits.filter(limits => !limits.deleted).map(limits => limits.request))
  }

  hostnameOrApiMaxExceeded = () => {
    const {configs} = this.state
    const filteredConfigs = configs.filter(config => !config.deleted)
    if (filteredConfigs.length === 2) {
      return (filteredConfigs[0].apiChecked && filteredConfigs[1].apiChecked) || (!isNullyField(filteredConfigs[0].hostname) && !isNullyField(filteredConfigs[1].hostname))
    } else {
      return false
    }
  }

  invalidEmptyHostname = () => {
    const invalid = this.state.configs.filter((config,index) => !config.deleted && isNullyField(config.hostname) && !config.apiChecked)
    return invalid.length !== 0
  }

  adjustedConfigIndex = (index) => {
    const totalDeleted = this.state.configs.filter(config => config.deleted).length
    return index - totalDeleted
  }

  validationClass = (config, field) => {
      const {invalidEmptyHostname, hostnameOrApiMaxExceeded} = this.state.validationErrors
      if (!this.state.dirty || this.state.isValid) {
          return ''
      }

      if (field === 'hostname') {
          if (invalidEmptyHostname && isNullyField(config.hostname)) {
              return 'has-error'
          } else if (hostnameOrApiMaxExceeded && !isNullyField(config.hostname)) {
              return 'has-error'
          } else {
              return ''
          }
      } else if (field === 'apiChecked') {
          if (hostnameOrApiMaxExceeded && config.apiChecked) {
            return 'has-error'
          } else {
            return ''
          }
      } else {
        return ''
      }
  }

   otherApiConfigFound = (index) => {
      const {configs} = this.state
      const adjustedIndex = this.adjustedConfigIndex(index)
      const filteredConfigs = configs.filter(config => !config.deleted)
      const indexToSearch = adjustedIndex === 1 ? 0 : 1
      return filteredConfigs.length > 1 && filteredConfigs[indexToSearch].apiChecked
  }

  oldConfigsFound = () => {
    return this.state.configs.filter(config => !config.deleted && !config.isNew).length > 0
  }

  render() {
    const {configs, isLoading} = this.state
    const {accountId} = this.props

    return (<div className="container-fluid instant-audit-config-editor">
      {isLoading && <div className="text-center" style={{marginTop: '25px'}}>
        <span style={{fontSize: '25px'}} className="fas fa-spinner fa-spin"></span>
      </div>}
      {!isLoading && <div className="" style={{marginTop: '25px'}}>
        {this.state.dirty && this.state.validationErrors.hostnameOrApiMaxExceeded &&
          <div className={'alert alert-danger'}>
            <strong>Warning:&nbsp;</strong>
            Each account is allowed exactly one api and hostname config!
            {/*TODO: this is stupid. I believe it's due to the api response format*/}
          </div>
        }
        <button className="btn btn-success pull-right" disabled={configs.filter(config => !config.deleted).length === 2}
                onClick={this.addConfig}><span className="far fa-plus"></span> ADD CONFIG
        </button>

        {configs.filter(config => !config.deleted).length === 0 && <div style={{textAlign: 'center'}}>
          <h5>No Instant Audit Tokens. Add one to get started.</h5>
        </div>}

        {configs.map((config, index) => {
          const toggleClass = config.collapse ? " fa-plus-circle" : " fa-minus-circle"
          const inConfig = (value) => {
            return buildUpdate('configs', value, index)
          }
          return (
            <table key={'config-table-' + index} className={"table" + hideClass(config)}>
              <thead>
              <tr className={"cm-report-table-header"}>
                <th>
                  <span className={'fas fa-lg' + toggleClass} onClick={() => this.toggleCollapse(index)}></span> Config
                  # {this.adjustedConfigIndex(index + 1)}
                </th>
                <th>
                  <span className={'fas fa-trash-alt fa-lg pull-right'} style={{color: '#6d808d', cursor: 'pointer'}}
                        onClick={() => this.onRemove(inConfig(buildUpdate('value', null, null, true)))}></span>
                </th>
              </tr>
              </thead>
              {!config.collapse &&
              <tbody>
              {!this.otherApiConfigFound(index) && (config.isNew || config.apiChecked) &&
                <tr className={this.validationClass(config,'apiChecked')}>
                  <td colSpan="2">
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" disabled={this.oldConfigsFound()} name="apiChecked"
                        checked={config.apiChecked}
                        onChange={e => {
                          this.onChange(inConfig(buildUpdate('value', {
                            apiChecked: e.target.checked,
                            hostname: e.target.checked ? '' : config.hostname
                          }, null, true)))
                        }} />
                      <label className="form-check-label" for="apiChecked">API</label>
                    </div>
                  </td>
                </tr>
              }
              {!config.apiChecked && <tr className={this.validationClass(config,'hostname')}>
                <td><label className={'control-label'}>Host Name*:</label></td>
                <td>
                  <input name={'hostname'} type="text" aria-label="Host Name"
                         className="form-control"
                         disabled={!config.isNew}
                         placeholder="mybrand.com or brand.audits.chatmeter.com"
                         required={!config.apiChecked}
                         value={config.hostname}
                         onChange={e => this.onChange(inConfig(buildUpdate('value', {hostname: e.target.value}, null, true)))}/>
                </td>
              </tr>}
              <tr>
                <td>
                  <label>Account:</label>
                </td>
                <td>
                  <input readOnly name={'accountId'} type="text" aria-label="Account" className="form-control"
                         value={accountId}/>
                </td>
              </tr>
              <tr>
                <td><label>Token:</label></td>
                <td>
                  {!!config.apiToken ?
                    <input readOnly name={'apiToken'} type="text" aria-label="Token" className="form-control"
                           value={config.apiToken}/> :
                    <input readOnly name={'apiTokenPlaceholder'} type="text" aria-label="Token"
                           className="form-control" value={"SAVE TO GENERATE TOKEN"}/>
                  }

                </td>
              </tr>
              {
                config.apiChecked ? <React.Fragment/> : <React.Fragment>
                <tr>
                  <td>
                    <h5 className="rate-limit-heading">Per User Rate Limits</h5>
                    {config.rateLimits.length ?
                      <React.Fragment /> :
                      <p className="text-muted">Default limits will be used if none are specified</p>
                    }
                  </td>
                  <td>
                    <button className="btn btn-secondary pull-right"
                            disabled={config.rateLimits.filter(limits => !limits.deleted).length > 1}
                            onClick={() => this.onAdd(inConfig(buildUpdate('rateLimits', {
                              value: defaultRateLimits(this.availableRequestTypes(config.rateLimits)),
                              isFinal: true
                            })))}><span className="far fa-plus"></span> ADD
                    </button>
                  </td>

                </tr>
                {config.rateLimits.map((rateLimits, rlIndex) => {
                  const inRateLimits = (value) => {
                    return inConfig(buildUpdate('rateLimits', value, rlIndex))
                  }

                  const key = 'rate-limit-individual' + index + '-' + rlIndex
                  return (
                    <tr key={key} className={'instant-audit-config-editor__column--indented '+hideClass(rateLimits)}>
                      <td colSpan="2">
                        <RateLimits key={key} onRemove={(value) => this.onRemove({
                          configs: {rateLimits: value, index: rlIndex},
                          index
                        })}
                                    onAdd={(value) => this.onAdd(inRateLimits(value))}
                                    onChange={(value) => this.onChange(inRateLimits(value))}
                                    rateLimits={rateLimits}
                                    availableRequestTypes={this.availableRequestTypes(config.rateLimits)}
                                    index={rlIndex}/>
                      </td>
                    </tr>
                  )
                })}
                </React.Fragment>
              }
              <tr>
                <td>
                  <h5 className="rate-limit-heading">Global Rate Limits</h5>
                  {config.hostnameRateLimits.length ?
                    <React.Fragment /> :
                    <p className="text-muted">Default limits will be used if none are specified</p>
                  }
                </td>
                <td>
                  <button className="btn btn-secondary pull-right"
                          disabled={config.hostnameRateLimits.filter(limits => !limits.deleted).length > 1}
                          onClick={() => this.onAdd(inConfig(buildUpdate('hostnameRateLimits', {
                            value: defaultRateLimits(this.availableRequestTypes(config.hostnameRateLimits)),
                            isFinal: true
                          })))}><span
                    className="far fa-plus"></span> ADD
                  </button>
                </td>
              </tr>
              {config.hostnameRateLimits.map((rateLimits, rlIndex) => {
                const inHostnameRateLimits = (value) => {
                  return inConfig(buildUpdate('hostnameRateLimits', value, rlIndex))
                }
                const key = 'rate-limit-hosted' + index + '-' + rlIndex
                return (
                  <tr key={key} className={hideClass(rateLimits)}>
                    <td colSpan="2" className="instant-audit-config-editor__column instant-audit-config-editor__column--indented">
                      <RateLimits key={key} type="hostnameRateLimits"
                                  onRemove={(value) => this.onRemove(inHostnameRateLimits(value))}
                                  onAdd={(value) => this.onAdd(inHostnameRateLimits(value))}
                                  onChange={(value) => this.onChange(inHostnameRateLimits(value))}
                                  rateLimits={rateLimits}
                                  availableRequestTypes={this.availableRequestTypes(config.hostnameRateLimits)}
                      />
                    </td>
                  </tr>
                )
              })}
              </tbody>
              }
            </table>
          )
        })}
      </div>}
    </div>)
  }
}





